import { useState, useRef, useEffect } from 'react';
import { MAX_DAILY_SEARCHES } from '../lib/searchLimitService';

const SUGGESTIONS = [
  'Machine Learning',
  'Compound Interest',
  'Blockchain',
  'Climate Change',
  'Quantum Computing',
  'Stoic Philosophy',
  'Gut Microbiome',
  'Options Trading',
  'Prompt Engineering',
  'Sleep Science',
];

// AI model defaults to openai; no UI selector shown on the main form

const FORMATS = [
  { id: 'text',   icon: '📖', name: 'Text',   tooltip: 'AI-generated lesson'                           },
  { id: 'audio',  icon: '🎧', name: 'Audio',  tooltip: 'Listen to your AI lesson'                      },
  { id: 'live',   icon: '🔴', name: 'Live',   tooltip: 'Real-time web search — paid plans only', paid: true },
  { id: 'visual', icon: '🎬', name: 'Visual', tooltip: 'Coming soon — AI-powered visual lessons', disabled: true },
];

// Microphone SVG icon
function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
      <path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0V5a2 2 0 0 0-2-2z"/>
      <path d="M19 11a1 1 0 0 1 1 1 8 8 0 0 1-7 7.938V22h2a1 1 0 0 1 0 2H9a1 1 0 0 1 0-2h2v-2.062A8 8 0 0 1 4 12a1 1 0 0 1 2 0 6 6 0 0 0 12 0 1 1 0 0 1 1-1z"/>
    </svg>
  );
}

function SearchForm({ onSearch, loading, dailyCount, limitReached, onUpgrade, tier, userRole }) {
  const isPaidUser = (tier && tier !== 'free') || userRole === 'admin' || userRole === 'super_admin';
  const [subject,          setSubject]          = useState('');
  const [format,           setFormat]           = useState('text');
  const [listening,        setListening]        = useState(false);
  const [voiceSupported,   setVoiceSupported]   = useState(false);
  const [showSuggestions,  setShowSuggestions]  = useState(false);
  const recognitionRef  = useRef(null);
  const dropdownRef     = useRef(null);

  useEffect(() => {
    setVoiceSupported(!!(window.SpeechRecognition || window.webkitSpeechRecognition));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredSuggestions = subject.trim()
    ? SUGGESTIONS.filter(s => s.toLowerCase().includes(subject.toLowerCase())).slice(0, 6)
    : SUGGESTIONS.slice(0, 6);

  const handleSuggestionClick = (s) => {
    setSubject(s);
    setShowSuggestions(false);
  };

  const toggleVoice = () => {
    if (listening) { recognitionRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang           = 'en-US';
    rec.continuous     = false;
    rec.interimResults = true;
    rec.onstart  = () => setListening(true);
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setSubject(transcript);
    };
    rec.onend    = () => setListening(false);
    rec.onerror  = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!subject.trim() || loading || limitReached) return;
    // Live mode is paid-only — redirect free users to upgrade
    if (format === 'live' && !isPaidUser) {
      onUpgrade?.();
      return;
    }
    // Live mode always uses Perplexity (real-time web search); other formats default to OpenAI
    const aiModel = format === 'live' ? 'perplexity' : 'openai';
    onSearch({ subject: subject.trim(), aiModel, format, level: 'beginner' });
  };

  // Counter colour
  const remaining = MAX_DAILY_SEARCHES - (dailyCount || 0);
  const counterClass =
    remaining <= 0   ? 'search-counter search-counter--empty' :
    remaining <= 2   ? 'search-counter search-counter--low'   :
                       'search-counter';

  return (
    <form className="search-card" onSubmit={handleSubmit}>

      {/* ── Hero: Subject ── */}
      <div className="input-group subject-group" ref={dropdownRef}>
        <label className="subject-label" htmlFor="subject">What do you want to learn?</label>
        <div className="subject-input-wrapper">
          <span className="input-icon">🔍</span>
          <input
            id="subject"
            type="text"
            className={`subject-input subject-input--hero${(subject || voiceSupported) ? ' subject-input--with-actions' : ''}`}
            placeholder="e.g. How does machine learning work?"
            value={subject}
            onChange={e => { setSubject(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            disabled={loading || limitReached}
            autoComplete="off"
            autoCorrect="off"
          />
          <div className="input-actions">
            {subject && !loading && (
              <button type="button" className="input-action-btn input-clear-btn" onClick={() => { setSubject(''); setShowSuggestions(true); }} aria-label="Clear">✕</button>
            )}
            {voiceSupported && (
              <button
                type="button"
                className={`input-action-btn input-mic-btn${listening ? ' input-mic-btn--active' : ''}`}
                onClick={toggleVoice}
                disabled={loading || limitReached}
                aria-label={listening ? 'Stop listening' : 'Speak your topic'}
              >
                <MicIcon />
                {listening && <span className="mic-ripple" />}
              </button>
            )}
          </div>
        </div>

        {/* ── Inline suggestion dropdown ── */}
        {showSuggestions && filteredSuggestions.length > 0 && !loading && !limitReached && (
          <div className="suggestions-dropdown" role="listbox" aria-label="Topic suggestions">
            <div className="suggestions-dropdown-label">💡 Try one of these</div>
            {filteredSuggestions.map(s => (
              <button
                type="button"
                key={s}
                className="suggestions-dropdown-item"
                onMouseDown={() => handleSuggestionClick(s)}
                role="option"
                aria-selected={false}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Format — compact icon row ── */}
      <div className="format-icon-row">
        {FORMATS.map(f => f.disabled ? (
          <div key={f.id} className="format-icon-btn format-icon-btn--disabled" data-tooltip={f.tooltip}>
            {f.icon}
          </div>
        ) : (
          <label key={f.id} className="format-icon-option">
            <input type="radio" name="format" value={f.id} checked={format === f.id} onChange={() => setFormat(f.id)} disabled={loading} />
            <div className={`format-icon-btn${format === f.id ? ' format-icon-btn--active' : ''}${f.paid && !isPaidUser ? ' format-icon-btn--paid' : ''}`} data-tooltip={f.tooltip}>
              {f.icon}
              {f.paid && !isPaidUser && <span className="format-paid-badge">Pro</span>}
            </div>
          </label>
        ))}
      </div>

      {/* ── Submit + counter ── */}
      <div className="submit-row">
        <button
          type="submit"
          className="learn-btn"
          disabled={loading || !subject.trim() || limitReached}
        >
          {loading ? (
            <><span className="spinner" /> {format === 'live' ? 'Searching the web…' : format === 'audio' ? 'Preparing your audio lesson…' : 'Generating your lesson…'}</>
          ) : limitReached ? (
            <>🚫 Daily limit reached</>
          ) : format === 'live' && !isPaidUser ? (
            <>⚡ Upgrade to Search Live</>
          ) : (
            <>{format === 'live' ? '🔴 Search Live' : format === 'audio' ? '🎧 Listen Now' : '⚡ Learn Now'}</>
          )}
        </button>

        {dailyCount !== undefined && (
          <span className={counterClass}>
            {remaining > 0
              ? `${dailyCount} / ${MAX_DAILY_SEARCHES} searches today`
              : `Limit reached · resets tomorrow`}
          </span>
        )}
      </div>

      {/* Limit reached message */}
      {limitReached && (
        <div className="limit-reached-msg">
          <div className="limit-reached-top">
            <span className="limit-reached-icon">🌙</span>
            <div className="limit-reached-text">
              <strong>You've used all {MAX_DAILY_SEARCHES} free lessons for today!</strong>
              <p>Your daily limit resets at midnight. Come back then — or unlock <strong>unlimited learning</strong> right now.</p>
            </div>
          </div>
          <div className="limit-reached-actions">
            {onUpgrade && (
              <button className="limit-upgrade-btn" onClick={onUpgrade}>
                ⚡ Upgrade for Unlimited Access
              </button>
            )}
            <span className="limit-resets-note">🔄 Free limit resets tomorrow at midnight</span>
          </div>
        </div>
      )}

    </form>
  );
}

export default SearchForm;
